import React from 'react';
import { Link } from 'react-router-dom';

const APP_NAME = import.meta.env.VITE_APP_NAME === 'plotmagic' ? 'PlotMagic' : 'RRD Planners';

/**
 * Shared auth layout shell with split-panel design.
 * Left: gold gradient with branding + plot preview
 * Right: dark panel with form content (children)
 */
export const AuthShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <div style={{
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      minHeight: '100vh',
      display: 'flex',
      background: '#0d0d0d',
    }}>
      {/* ── Left Panel (Gold gradient) ── */}
      <div className="auth-left-panel" style={{
        flex: 1,
        background: 'linear-gradient(135deg, #FFD93D 0%, #f0c420 50%, #e6b800 100%)',
        padding: 60,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: '-50%', right: '-20%', width: 500, height: 500, background: 'rgba(255,255,255,0.08)', borderRadius: '50%' }} />
        <div style={{ position: 'absolute', bottom: '-30%', left: '-10%', width: 400, height: 400, background: 'rgba(255,255,255,0.06)', borderRadius: '50%' }} />

        <div style={{ position: 'relative', zIndex: 1 }}>
          <h1 style={{ fontSize: 36, fontWeight: 800, color: '#1a1a1a', fontStyle: 'italic', letterSpacing: -0.5 }}>
            {APP_NAME}
          </h1>
          <div style={{ fontSize: 13, color: 'rgba(26,26,26,0.6)', textTransform: 'uppercase', letterSpacing: 3, marginTop: 6, fontWeight: 600 }}>
            Plot Map Generator
          </div>
        </div>

        <div style={{ position: 'relative', zIndex: 1 }}>
          <h2 style={{ fontSize: 34, fontWeight: 800, color: '#1a1a1a', marginBottom: 16, lineHeight: 1.2 }}>
            Accurate plots,<br />professional maps
          </h2>
          <p style={{ fontSize: 16, color: 'rgba(26,26,26,0.7)', lineHeight: 1.7, maxWidth: 440 }}>
            Generate A4 registration-ready plot maps with precise dimensions, auto-scaling, and print-ready PDF exports.
          </p>

          <div style={{ display: 'flex', gap: 20, marginTop: 32, flexWrap: 'wrap' }}>
            {[
              { icon: <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#FFD93D" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="9" y1="21" x2="9" y2="9" /></svg>, label: 'AutoCAD-style\nprecision' },
              { icon: <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#FFD93D" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>, label: 'Print-ready\nPDF export' },
              { icon: <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#FFD93D" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>, label: 'Save & manage\nmultiple plots' },
            ].map((f, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'rgba(26,26,26,0.08)', padding: '10px 16px', borderRadius: 10 }}>
                <div style={{ width: 36, height: 36, background: '#1a1a1a', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {f.icon}
                </div>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a', whiteSpace: 'pre-line' }}>{f.label}</span>
              </div>
            ))}
          </div>

          {/* Plot preview */}
          <div style={{ marginTop: 40, background: '#1a1a1a', borderRadius: 16, padding: 24, boxShadow: '0 24px 60px rgba(0,0,0,0.25)', position: 'relative', zIndex: 1 }}>
            <svg width="100%" height="180" viewBox="0 0 400 180" style={{ background: '#111', borderRadius: 8 }}>
              <rect x="60" y="25" width="280" height="130" fill="none" stroke="#FFD93D" strokeWidth="2" />
              <circle cx="60" cy="25" r="4" fill="#FFD93D" />
              <circle cx="340" cy="25" r="4" fill="#FFD93D" />
              <circle cx="340" cy="155" r="4" fill="#FFD93D" />
              <circle cx="60" cy="155" r="4" fill="#FFD93D" />
              <line x1="60" y1="170" x2="340" y2="170" stroke="#666" strokeWidth="1" />
              <text x="200" y="178" fill="#888" textAnchor="middle" fontSize="10">30'-0"</text>
              <line x1="45" y1="25" x2="45" y2="155" stroke="#666" strokeWidth="1" />
              <text x="38" y="95" fill="#888" textAnchor="middle" fontSize="10" transform="rotate(-90, 38, 95)">50'-0"</text>
              <rect x="80" y="40" width="160" height="100" fill="rgba(255,217,61,0.05)" stroke="#444" strokeWidth="1" strokeDasharray="4" />
              <text x="160" y="85" fill="#555" textAnchor="middle" fontSize="11">ROOM</text>
              <text x="160" y="100" fill="#444" textAnchor="middle" fontSize="9">A.C.C ROOF</text>
              <text x="310" y="95" fill="#444" textAnchor="middle" fontSize="9">OPEN</text>
              <polygon points="370,20 375,35 365,35" fill="#FFD93D" />
              <text x="370" y="45" fill="#888" textAnchor="middle" fontSize="9">N</text>
            </svg>
          </div>
        </div>
      </div>

      {/* ── Right Panel (Dark form area) ── */}
      <div className="auth-right-panel" style={{
        flex: 1,
        background: '#1a1a1a',
        padding: '40px 60px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflowY: 'auto',
      }}>
        <div style={{ position: 'absolute', top: 0, left: 0, bottom: 0, width: 1, background: 'linear-gradient(to bottom, transparent, #333, transparent)' }} />

        {/* Back to Home */}
        <Link to="/" style={{
          position: 'absolute', top: 24, right: 28,
          display: 'inline-flex', alignItems: 'center', gap: 5,
          fontSize: 12, color: '#555', textDecoration: 'none', fontWeight: 500,
        }}
          onMouseEnter={e => (e.currentTarget.style.color = '#aaa')}
          onMouseLeave={e => (e.currentTarget.style.color = '#555')}
        >
          ← Back to Home
        </Link>

        <div style={{ width: '100%', maxWidth: 400 }}>
          {children}
        </div>

        <div style={{ position: 'absolute', bottom: 24, left: 0, right: 0, textAlign: 'center', color: '#333', fontSize: 12 }}>
          &copy; {new Date().getFullYear()} {APP_NAME}. All rights reserved.
        </div>
      </div>

      <style>{`
        @media (max-width: 968px) {
          .auth-left-panel { display: none !important; }
          .auth-right-panel { flex: 1 !important; padding: 40px 24px !important; }
        }
      `}</style>
    </div>
  );
};

/** Shared input styling for auth forms */
export const authInputStyle: React.CSSProperties = {
  width: '100%', padding: '12px 14px', background: '#242424', border: '1px solid #333',
  borderRadius: 10, fontSize: 15, color: '#fff', fontFamily: 'inherit',
  outline: 'none', transition: 'all 0.3s',
};

export const authInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
  e.target.style.borderColor = '#FFD93D';
  e.target.style.background = '#2a2a2a';
  e.target.style.boxShadow = '0 0 0 3px rgba(255,217,61,0.1)';
};

export const authInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
  e.target.style.borderColor = '#333';
  e.target.style.background = '#242424';
  e.target.style.boxShadow = 'none';
};

export const authLabelStyle: React.CSSProperties = {
  display: 'block', marginBottom: 6, color: '#aaa', fontSize: 12,
  fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.5,
};

export const authButtonStyle = (disabled: boolean): React.CSSProperties => ({
  width: '100%', padding: '14px', background: disabled ? '#b8960e' : '#FFD93D',
  color: '#1a1a1a', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700,
  cursor: disabled ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
  transition: 'all 0.3s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
});
